/**
 * Calendar period helpers for rolling budgets.
 * Weeks start Monday (ISO). Monthly/yearly use calendar bounds.
 */

import {
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
  endOfWeek,
} from 'date-fns';
import type { BudgetPeriodType } from '../types/budget.types';

export interface PeriodBounds {
  startDate: string;
  endDate: string;
  label: string;
}

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function parseAnchor(anchorDate?: string | Date): Date {
  if (!anchorDate) return new Date();
  if (anchorDate instanceof Date) return anchorDate;
  return parseISO(anchorDate);
}

/**
 * Resolve inclusive start/end (yyyy-MM-dd) and a human label for the period containing anchor.
 */
export function getPeriodBounds(
  periodType: BudgetPeriodType,
  anchorDate?: string | Date
): PeriodBounds {
  const anchor = parseAnchor(anchorDate);

  if (periodType === 'weekly') {
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    const end = endOfWeek(anchor, { weekStartsOn: 1 });
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`,
    };
  }

  if (periodType === 'yearly') {
    const start = startOfYear(anchor);
    const end = endOfYear(anchor);
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: format(anchor, 'yyyy'),
    };
  }

  // monthly
  const start = startOfMonth(anchor);
  const end = endOfMonth(anchor);
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
    label: format(anchor, 'MMMM yyyy'),
  };
}

/**
 * Shift the period by `delta` steps (e.g. -1 previous, +1 next).
 * Returns a new ISO date (yyyy-MM-dd) inside the shifted period — mid-period for stability.
 */
export function shiftPeriod(
  periodType: BudgetPeriodType,
  anchorDate: string | Date,
  delta: number
): string {
  const anchor = parseAnchor(anchorDate);

  if (periodType === 'weekly') {
    return toIsoDate(addWeeks(anchor, delta));
  }
  if (periodType === 'yearly') {
    return toIsoDate(addYears(anchor, delta));
  }
  // monthly — preserve day-of-month where possible; clamp via date-fns
  return toIsoDate(addMonths(anchor, delta));
}

/** Today's date as yyyy-MM-dd (local). */
export function todayIsoDate(): string {
  return toIsoDate(new Date());
}

/** Mid-point date string for a period (useful as a stable anchor). */
export function midPeriodAnchor(periodType: BudgetPeriodType, anchorDate?: string | Date): string {
  const { startDate, endDate } = getPeriodBounds(periodType, anchorDate);
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const midMs = (start.getTime() + end.getTime()) / 2;
  return toIsoDate(new Date(midMs));
}

/** How many times the grain fits in a year (fixed cashflow fractions). */
const ANNUAL_MULTIPLIER: Record<BudgetPeriodType, number> = {
  weekly: 52,
  monthly: 12,
  yearly: 1,
};

/** Convert a stored grain amount to an annual total. */
export function toAnnualBudgetAmount(amount: number, periodType: BudgetPeriodType): number {
  return amount * ANNUAL_MULTIPLIER[periodType];
}

/** Convert an annual total into a grain amount. */
export function fromAnnualBudgetAmount(annual: number, periodType: BudgetPeriodType): number {
  return annual / ANNUAL_MULTIPLIER[periodType];
}

/**
 * Scale a plan limit from its stored grain into a view grain.
 * e.g. $100 monthly → weekly = 100 × 12 / 52
 */
export function scaleBudgetAmount(
  amount: number,
  from: BudgetPeriodType,
  to: BudgetPeriodType
): number {
  if (from === to) return roundMoney(amount);
  const annual = toAnnualBudgetAmount(amount, from);
  return roundMoney(fromAnnualBudgetAmount(annual, to));
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
